import { parseAttributeList, } from './attributes.js';
import { getLayout, setAttributeSource, setHeaderOrder, setLayout, } from './metadata.js';
const VARIANT_ATTRS = [
    'BANDWIDTH',
    'AVERAGE-BANDWIDTH',
    'RESOLUTION',
    'FRAME-RATE',
    'CODECS',
    'VIDEO',
    'AUDIO',
    'SUBTITLES',
];
const MEDIA_ATTRS = [
    'TYPE',
    'GROUP-ID',
    'NAME',
    'LANGUAGE',
    'DEFAULT',
    'AUTOSELECT',
    'FORCED',
    'URI',
];
const IFRAME_ATTRS = [
    'BANDWIDTH',
    'AVERAGE-BANDWIDTH',
    'RESOLUTION',
    'CODECS',
    'VIDEO',
    'URI',
];
/** Attribute names the variant model can express — see `mergeAttributes`. */
export const VARIANT_MODELED_ATTRS = VARIANT_ATTRS;
/** Attribute names the media model can express. */
export const MEDIA_MODELED_ATTRS = MEDIA_ATTRS;
/** Attribute names the I-frame stream model can express. */
export const IFRAME_MODELED_ATTRS = IFRAME_ATTRS;
const MEDIA_TYPES = new Set(['AUDIO', 'VIDEO', 'SUBTITLES', 'CLOSED-CAPTIONS']);
/**
 * Parse a master playlist.
 *
 * The model is lossless: every line either becomes a modeled entry or is kept
 * verbatim in the playlist's layout (see {@link getMasterLayout}), so
 * `buildMasterPlaylist(parseMasterPlaylist(text))` reproduces `text` — modulo
 * blank lines, leading/trailing whitespace, and a `LANGUAGE` case fold.
 */
export function parseMasterPlaylist(content) {
    const lines = content.split('\n').map((l) => l.trim());
    const variants = [];
    const media = [];
    const iFrameStreams = [];
    const layout = [];
    const headerOrder = [];
    let version;
    let independentSegments = false;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line === '' || line === '#EXTM3U')
            continue;
        if (line.startsWith('#EXT-X-VERSION:')) {
            const parsed = Number(line.slice('#EXT-X-VERSION:'.length).trim());
            if (Number.isFinite(parsed)) {
                version = parsed;
                headerOrder.push('#EXT-X-VERSION');
                continue;
            }
            layout.push(line);
            continue;
        }
        if (line === '#EXT-X-INDEPENDENT-SEGMENTS') {
            independentSegments = true;
            headerOrder.push('#EXT-X-INDEPENDENT-SEGMENTS');
            continue;
        }
        if (line.startsWith('#EXT-X-MEDIA:')) {
            const attrs = parseAttributeList(line.slice('#EXT-X-MEDIA:'.length));
            const entry = buildMediaEntry(attrs);
            if (!entry) {
                // Unknown TYPE — keep the line rather than silently dropping it.
                layout.push(line);
                continue;
            }
            media.push(entry);
            layout.push(entry);
            continue;
        }
        if (line.startsWith('#EXT-X-STREAM-INF:')) {
            // The URI is the next line, skipping blanks only. Scanning past a
            // tag would let a URI-less STREAM-INF steal the following
            // variant's URI, which is how this parser used to misread a
            // truncated playlist.
            let j = i + 1;
            while (j < lines.length && lines[j] === '')
                j++;
            const uri = j < lines.length && !lines[j].startsWith('#')
                ? lines[j]
                : undefined;
            const attrs = parseAttributeList(line.slice('#EXT-X-STREAM-INF:'.length));
            const variant = uri ? buildVariant(attrs, uri) : undefined;
            if (!variant) {
                layout.push(line);
                continue;
            }
            variants.push(variant);
            layout.push(variant);
            i = j;
            continue;
        }
        if (line.startsWith('#EXT-X-I-FRAME-STREAM-INF:')) {
            const attrs = parseAttributeList(line.slice('#EXT-X-I-FRAME-STREAM-INF:'.length));
            const entry = buildIFrameStream(attrs);
            if (!entry) {
                layout.push(line);
                continue;
            }
            iFrameStreams.push(entry);
            layout.push(entry);
            continue;
        }
        layout.push(line);
    }
    const master = {
        variants,
        media,
        audioGroups: media.filter((m) => m.type === 'AUDIO'),
        videoGroups: media.filter((m) => m.type === 'VIDEO'),
        ...(version !== undefined ? { version } : {}),
        ...(independentSegments ? { independentSegments: true } : {}),
        ...(iFrameStreams.length > 0 ? { iFrameStreams } : {}),
    };
    setLayout(master, layout);
    setHeaderOrder(master, headerOrder);
    return master;
}
/**
 * The playlist's line order, including any lines the model does not represent.
 *
 * Entries are the same objects held in `media` / `variants` / `iFrameStreams`,
 * so removing one from those arrays removes it from the output too, and adding
 * one appends it. Returns `undefined` for a model that was not parsed from
 * text — the builder then falls back to a canonical layout.
 */
export function getMasterLayout(master) {
    return getLayout(master);
}
/** Fix the line order of a master assembled in code (see {@link getMasterLayout}). */
export function setMasterLayout(master, items) {
    setLayout(master, items);
}
function buildMediaEntry(attrs) {
    const type = plain(attrs, 'TYPE');
    if (!type || !MEDIA_TYPES.has(type))
        return undefined;
    const language = quoted(attrs, 'LANGUAGE');
    const uri = quoted(attrs, 'URI');
    const entry = {
        type: type,
        groupId: quoted(attrs, 'GROUP-ID') ?? '',
        name: quoted(attrs, 'NAME') ?? '',
        ...(language ? { language } : {}),
        ...(uri ? { uri } : {}),
        ...flag(attrs, 'DEFAULT', 'default'),
        ...flag(attrs, 'AUTOSELECT', 'autoselect'),
        ...flag(attrs, 'FORCED', 'forced'),
    };
    setAttributeSource(entry, attrs);
    return entry;
}
function buildVariant(attrs, uri) {
    const bandwidth = numeric(attrs, 'BANDWIDTH');
    if (bandwidth === undefined)
        return undefined;
    const resolution = plain(attrs, 'RESOLUTION');
    const codecs = quoted(attrs, 'CODECS');
    const averageBandwidth = numeric(attrs, 'AVERAGE-BANDWIDTH');
    const frameRate = numeric(attrs, 'FRAME-RATE');
    const videoGroup = quoted(attrs, 'VIDEO');
    const audioGroup = quoted(attrs, 'AUDIO');
    const subtitlesGroup = quoted(attrs, 'SUBTITLES');
    const resolutionParsed = resolution
        ? parseResolution(resolution)
        : undefined;
    const variant = {
        bandwidth,
        ...(averageBandwidth !== undefined ? { averageBandwidth } : {}),
        ...(resolution ? { resolution } : {}),
        ...(resolutionParsed ? { resolutionParsed } : {}),
        ...(frameRate !== undefined ? { frameRate } : {}),
        ...(codecs ? { codecs } : {}),
        ...(videoGroup ? { videoGroup } : {}),
        ...(audioGroup ? { audioGroup } : {}),
        ...(subtitlesGroup ? { subtitlesGroup } : {}),
        uri,
    };
    setAttributeSource(variant, attrs);
    return variant;
}
function buildIFrameStream(attrs) {
    const bandwidth = numeric(attrs, 'BANDWIDTH');
    const uri = quoted(attrs, 'URI');
    if (bandwidth === undefined || !uri)
        return undefined;
    const resolution = plain(attrs, 'RESOLUTION');
    const averageBandwidth = numeric(attrs, 'AVERAGE-BANDWIDTH');
    const codecs = quoted(attrs, 'CODECS');
    const videoGroup = quoted(attrs, 'VIDEO');
    const resolutionParsed = resolution
        ? parseResolution(resolution)
        : undefined;
    const entry = {
        bandwidth,
        ...(averageBandwidth !== undefined ? { averageBandwidth } : {}),
        ...(resolution ? { resolution } : {}),
        ...(resolutionParsed ? { resolutionParsed } : {}),
        ...(codecs ? { codecs } : {}),
        ...(videoGroup ? { videoGroup } : {}),
        uri,
    };
    setAttributeSource(entry, attrs);
    return entry;
}
/** `'1280x720'` → `{ width: 1280, height: 720 }`; `undefined` when malformed. */
export function parseResolution(raw) {
    const match = raw.trim().match(/^(\d+)\s*[xX]\s*(\d+)$/);
    if (!match)
        return undefined;
    return { width: Number(match[1]), height: Number(match[2]) };
}
/** `{ width, height }` → `'1280x720'`. */
export function formatResolution(res) {
    return `${res.width}x${res.height}`;
}
function find(attrs, name) {
    return attrs.find((a) => a.name === name);
}
function quoted(attrs, name) {
    const a = find(attrs, name);
    return a && a.quoted ? a.value : undefined;
}
function plain(attrs, name) {
    const a = find(attrs, name);
    return a && !a.quoted && !a.bare ? a.value : undefined;
}
function numeric(attrs, name) {
    const raw = plain(attrs, name);
    if (raw === undefined || raw === '')
        return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
}
function flag(attrs, name, key) {
    // Quoted or not. The spec says enumerated-string, so unquoted, but real
    // packagers write `DEFAULT="YES"` too; refusing it dropped the attribute
    // on rebuild, and a master whose only default was written that way came
    // out with no default at all. The builder writes it back unquoted.
    const a = find(attrs, name);
    const raw = a && !a.bare ? a.value : undefined;
    if (raw === 'YES')
        return { [key]: true };
    if (raw === 'NO')
        return { [key]: false };
    return {};
}
//# sourceMappingURL=parse.js.map